import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Heart, Calendar, Star, ExternalLink, Share2, Target, MapPin, GraduationCap, UserRound, ChevronRight, LogOut, CircleHelp } from "lucide-react";
import { useMobile } from "../hooks/useMobile";
import { AccountActions } from "../components/AccountActions";
import { MobilePartnerBanner } from "../components/MobilePartnerBanner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StudentTutor } from "../components/StudentTutor";

export default function StudentDashboard() {
  const { user, logout } = useAuth();
  const mobile = useMobile();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [goal, setGoal] = useState("");

  const load = useCallback(() => api.get("/student/dashboard").then((r) => { setData(r.data); setGoal(r.data.future_goal || ""); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const saveGoal = async (e) => {
    e.preventDefault();
    try {
      const { data: res } = await api.put("/student/goal", { future_goal: goal });
      toast.success(res.message);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const sharePost = async (postId) => {
    try {
      const { data: res } = await api.post(`/posts/${postId}/share`);
      if (res.share_link) await navigator.clipboard.writeText(res.share_link).catch(() => {});
      if (res.points_earned) toast.success(`Link copied! ${res.message}`);
      else toast.info(res.message);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!data) return <div className="p-16 text-center text-slate-500">Loading...</div>;

  const stats = [
    { icon: Trophy, label: "Reward Points", value: data.points, bg: "#A3E635", testId: "student-points-card" },
    { icon: Heart, label: "Following", value: data.followed.length, bg: "#93C5FD" },
    { icon: Star, label: "Reviews Written", value: data.reviews.length, bg: "#FCD34D" },
    { icon: Calendar, label: "Updates", value: data.posts.length, bg: "#FDBA74" },
  ];

  return (
    <div className="student-page max-w-6xl mx-auto px-4 sm:px-8 py-12">
      {mobile && <div className="mobile-student-header" data-testid="mobile-student-profile"><span className="mobile-student-photo">{user?.picture ? <img src={user.picture} alt="" /> : <UserRound size={40} />}</span><div><h1>{user?.name}</h1><p><GraduationCap size={15} />Student{user?.city && ` · ${user.city}`}</p><span>DreamCots community member</span></div></div>}
      <h1 className="font-display text-4xl font-extrabold tracking-tight">Hi, {user?.name}</h1>
      <div className="student-stats mt-8 grid grid-cols-2 lg:grid-cols-4 gap-5">
        {stats.map(({ icon: Icon, label, value, bg, testId }) => (
          <div key={label} className="nb-stat" style={{ background: bg }} data-testid={testId}>
            <Icon className="w-5 h-5 mb-2" />
            <p className="font-display text-3xl font-extrabold">{value}</p>
            <p className="text-xs font-bold uppercase tracking-widest mt-1 opacity-70">{label}</p>
          </div>
        ))}
      </div>

      {mobile && <><div className="mobile-student-activity" data-testid="student-mobile-activity"><h2>My Activity</h2><div>{[[Heart, "Institutes You Follow", `${data.followed.length} institutes saved`, "#student-followed"], [Star, "My Reviews & Ratings", `${data.reviews.length} reviews written`, "#student-reviews"], [Calendar, "Institute Updates", `${data.posts.length} updates from your institutes`, "#student-updates"], [Target, "Target Exam & Standard", data.future_goal || "Set your learning goal", "#student-goal"]].map(([Icon, label, sub, href]) => <a key={href} data-testid={`activity-${href.slice(1)}`} href={href}><Icon /><span>{label}<small>{sub}</small></span><ChevronRight size={17} /></a>)}</div></div><div className="mt-6"><MobilePartnerBanner title="Boost admissions with DreamCots" label="For coaching & school owners" button="Register as Partner" testId="student-partner-banner" /></div></>}
      <section className="mt-12" id="student-goal">
        <div className="flex items-center gap-2 mb-4">
          <Target className="w-5 h-5 text-lime-600" />
          <h2 className="font-display text-2xl font-extrabold tracking-tight">Matched to Your Goal</h2>
        </div>
        <form onSubmit={saveGoal} className="flex flex-col sm:flex-row gap-2 max-w-xl mb-5">
          <input data-testid="goal-input" value={goal} onChange={(e) => setGoal(e.target.value)}
            placeholder="Your future goal — e.g. IIT-JEE 2027, NEET, Class 10 boards" className="nb-input flex-1" />
          <button data-testid="goal-save-btn" className="nb-btn nb-btn-lime nb-btn-sm">Save Goal</button>
        </form>
        {!data.future_goal ? (
          <div data-testid="goal-empty" className="nb-flat border-dashed p-8 text-center text-sm text-slate-500">
            Set your future goal above and we'll match institutes that prepare students for it.
          </div>
        ) : (data.recommended || []).length === 0 ? (
          <p data-testid="goal-no-matches" className="text-sm text-slate-500">
            No matches yet for "<span className="font-bold">{data.future_goal}</span>" — check back as more institutes list their courses and batches.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {data.recommended.map((r) => (
              <Link key={r.id} to={`/institutes/${r.id}`} data-testid={`goal-match-${r.id}`}
                className="nb p-5 hover:-translate-y-0.5 transition-transform duration-150" style={{ background: "#F2FBE3" }}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-display font-extrabold truncate">{r.name}</p>
                  <span className="nb-badge flex-shrink-0" style={{ background: "#A3E635" }}>{r.type === "school" ? "School" : "Coaching"}</span>
                </div>
                <p className="text-sm text-slate-500 mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {r.area}, {r.city}</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(r.match_terms || []).map((t) => <span key={t} className="nb-badge" style={{ background: "#FCD34D" }}>matches "{t}"</span>)}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <StudentTutor />

      <section className="mt-12" id="student-updates">
        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4">Updates from Institutes You Follow</h2>
        {data.posts.length === 0 ? (
          <div data-testid="student-posts-empty" className="nb-flat border-dashed p-10 text-center text-sm text-slate-500">
            Follow institutes from the <Link to="/institutes" className="font-bold underline underline-offset-2">directory</Link> to see their updates here.
          </div>
        ) : (
          <div className="space-y-4">
            {data.posts.map((e) => (
              <div key={e.id} data-testid={`student-post-${e.id}`} className="nb p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display font-extrabold">{e.title}</p>
                  <p className="text-sm text-slate-500 mt-1">{e.institution_name} · {(e.created_at || "").slice(0, 10)}</p>
                  {e.content && <p className="text-sm text-slate-600 mt-1">{e.content}</p>}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {e.link && (
                    <a href={e.link} target="_blank" rel="noreferrer" className="nb-btn nb-btn-lime nb-btn-sm">
                      <ExternalLink className="w-4 h-4" /> Open
                    </a>
                  )}
                  <button data-testid={`student-share-${e.id}`} onClick={() => sharePost(e.id)} className="nb-btn nb-btn-sm">
                    <Share2 className="w-4 h-4" /> Share (+pts)
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-12" id="student-followed">
        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4">Institutes You Follow</h2>
        {data.followed.length === 0 ? (
          <p className="text-sm text-slate-500">Not following anyone yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {data.followed.map((f) => (
              <Link key={f.id} to={`/institutes/${f.id}`} data-testid={`student-followed-${f.id}`} className="nb p-5 hover:-translate-y-0.5 transition-transform duration-150">
                <p className="font-display font-extrabold">{f.name}</p>
                <p className="text-sm text-slate-500 mt-1">{f.area}, {f.city} · <span className="capitalize">{f.type}</span></p>
              </Link>
            ))}
          </div>
        )}
      </section>
      {mobile && <><section className="mt-7" id="student-reviews" data-testid="student-mobile-reviews"><h2 className="font-display mb-3">My Reviews & Ratings</h2><div className="space-y-3">{data.reviews.map((r) => <div key={r.id} className="nb p-4" data-testid={`student-review-${r.id}`}><p className="font-medium">{r.institution_name || "Institute review"}</p><p className="text-xs text-slate-500 mt-1">{r.comment || "Ratings submitted"}</p><span className="text-xs text-lime-700">{r.verified ? "Published" : "Awaiting approval"}</span></div>)}{!data.reviews.length && <p className="mobile-empty">Your reviews will appear here.</p>}</div></section><Link data-testid="student-mobile-help" to="/contact" className="nb p-4 flex items-center gap-3 mt-6"><CircleHelp size={20} /><span className="flex-1">Help & Support</span><ChevronRight size={17} /></Link><button data-testid="student-mobile-logout" className="student-mobile-logout" onClick={() => { logout(); navigate("/"); }}><LogOut size={17} />Log Out</button><p className="text-center text-xs text-slate-400 mt-6">DREAMCOTS · For Indian Students & Educators</p></>}
      <AccountActions />
    </div>
  );
}
